import { useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid/AppDataGrid';
import { AppDialog } from '../../../components/AppDialog';
import { useNotify } from '../../../components/notifications';
import { useDeleteFlow } from '../../../components/confirm';
import { AppToolbar } from '../../../components/AppToolbar/AppToolbar';
import { PRE_LOGIN_FORM } from './PreLoginSettingsDetailPage';
import './preLoginSettings.css';
import type { PreLoginTest, PreLoginTestInput, PreLoginTestCatalogRow } from './types';

interface Props {
  /** The sample whose tests are being assigned; null = none picked yet. */
  sampleOid: string | null;
  /** Its display id, for the section heading. */
  sampleId: string | null;
  /** The sample's tests as loaded — one row per PARAMETER. */
  tests: PreLoginTest[];
  /** Re-reads the parent's tests / containers / parameters after a save. */
  onSaved: () => void;
  /** Toolbar Refresh - re-reads without saving. */
  onRefresh: () => void;
}

/** A test as the ASSIGNMENT grid shows it — one row per (test, method,
 *  component), carrying the parameter rows that make it up. */
interface AssignedTest {
  key:           string;
  test:          string | null;
  method:        string | null;
  component:     string | null;
  matrix:        string | null;
  /** Legacy TestGroupName column: the group a member belongs to. */
  testGroupName: string | null;
  /** Legacy "Parameter" column (TestParams): Default · AllParam · Customized. */
  parameter:     string | null;
  subOut:        boolean;
  /** How many parameters this test contributes — the number of COCSettingsTest
   *  rows that a Delete would remove. Shown so the count is never a surprise. */
  paramCount:    number;
  testMethodOid: string | null;
  rows:          PreLoginTest[];
}

/** Groups parameter rows into the tests a user actually assigns. */
function groupTests(rows: PreLoginTest[]): AssignedTest[] {
  const by = new Map<string, AssignedTest>();
  for (const r of rows) {
    // A GROUP test is one assignable item however many member tests it spans,
    // so it keys (and is named) by the group, not by each member.
    const isGrp = r.isGroup && !!r.testGroupName;
    const key = isGrp
      ? `G|${r.testGroupName}`
      : `${r.testMethodOid ?? r.test ?? ''}|${r.components ?? 'Default'}`;
    const hit = by.get(key);
    if (hit) {
      hit.rows.push(r);
      hit.paramCount = hit.rows.length;
      if (r.testParams === 'Customized') hit.parameter = 'Customized';
    } else {
      by.set(key, {
        key,
        test: isGrp ? `${r.testGroupName} (group)` : r.test,
        testGroupName: r.testGroupName, parameter: r.testParams,
        method: isGrp ? null : r.method, component: isGrp ? null : r.components, matrix: r.matrix,
        subOut: r.subOut, paramCount: 1, testMethodOid: r.testMethodOid, rows: [r],
      });
    }
  }
  return [...by.values()];
}

/**
 * **Test Assignment** — the second grid beneath the Samples grid, assigning
 * tests to the selected sample. Mirrors Sample Registration's own master-detail
 * (`SampleLoginView` → `TestAssignment`), which is the screen this one is meant
 * to feel like.
 *
 * <p><b>The grain differs from what is stored, on purpose.</b> A user assigns a
 * TEST, but `dbo.COCSettingsTest` is keyed on `Testparameter` — one row per
 * parameter. So this grid groups the stored rows into tests, Add expands a
 * chosen test back into its parameters, and Remove takes all of that test's
 * rows together. The Parameters tab is where the per-parameter detail belongs.</p>
 *
 * <p><b>Add and Remove save immediately</b> (owner request 2026-09-29: "Save tests
 * is not needed") — the picker's Add button / the delete confirmation IS the
 * commit, exactly like legacy's Test popup (`TestSelectionSave` on OK). The whole
 * resulting set goes in ONE PUT, so a test can never be half-added, and the
 * server re-syncs the sample's bottle rows in the same transaction.</p>
 */
export default function PreLoginTestAssignment({ sampleOid, sampleId, tests, onSaved, onRefresh }: Props) {
  const notify = useNotify();
  const runDelete = useDeleteFlow();

  const [sel, setSel] = useState<Set<string | number>>(new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setSel(new Set()); }, [tests]);

  const grouped = useMemo(() => groupTests(tests), [tests]);

  const columns: AppColumn<AssignedTest>[] = useMemo(() => [
    // Legacy COCSettingsTest_ListView_Copy_SampleRegistration, in its order:
    // SampleID (this grid follows the selected sample, so it is implied) · Matrix ·
    // Test · TestGroupName · Method · Components · SubOut · Parameter.
    { field: 'matrix',        header: 'Matrix',        width: 110 },
    { field: 'test',          header: 'Test',          width: 220 },
    { field: 'testGroupName', header: 'TestGroupName', width: 150 },
    { field: 'method',        header: 'Method',        width: 140 },
    { field: 'component',     header: 'Components',    width: 130 },
    // SubOut is the one editable cell (legacy batch edit); it saves with the set.
    { field: 'subOut',        header: 'SubOut',        width: 70,
      edit: { type: 'combo', options: ['true', 'false'], clearable: false } },
    { field: 'parameter',     header: 'Parameter',     width: 110 },
  ], []);

  const toInputs = (list: PreLoginTest[]): PreLoginTestInput[] =>
    list.map(r => ({
      oid:              r.oid.startsWith('new:') ? null : r.oid,
      testParameterOid: r.testParameterOid ?? '',
      groupTestOid:     r.groupTestOid,
      isGroup:          r.isGroup,
      subOut:           r.subOut,
      subLabOid:        r.subLabOid,
      suboutQCTypeOid:  r.suboutQCTypeOid,
      preservativeOid:  r.preservativeOid,
      containerTypeOid: r.containerTypeOid,
      containerCount:   r.containerCount,
      dupBottle:        r.dupBottle,
      unitOid:          null,
      mdl:              null,
      rptLimit:         null,
      df:               null,
    }));

  /** Legacy `checkIfGroupTestCustomized`: once only SOME members of a group
   *  remain, the group is no longer a group - the survivors are flattened to
   *  ordinary tests (IsGroup false, GroupTest cleared). */
  const flattenBroken = (before: PreLoginTest[], after: PreLoginTest[]): PreLoginTest[] => {
    const broken = new Set<string>();
    for (const r of before) {
      if (r.isGroup && r.testGroupName && !after.some(a => a.oid === r.oid)) broken.add(r.testGroupName);
    }
    if (broken.size === 0) return after;
    return after.map(r => (r.isGroup && r.testGroupName && broken.has(r.testGroupName)
      ? { ...r, isGroup: false, groupTestOid: null, testGroupName: null } : r));
  };

  /** PUTs the whole resulting set, then has the parent re-read. */
  const persist = async (next: PreLoginTest[]) => {
    if (!sampleOid) return;
    await api.put(`/api/v1/pre-login-settings/samples/${sampleOid}/tests`, toInputs(next));
    onSaved();
  };

  /** SubOut toggled on a test: applied to every parameter row behind it and saved. */
  const onCellCommit = (row: AssignedTest, col: AppColumn<AssignedTest>, value: string) => {
    if (col.field !== 'subOut') return;
    const on = value === 'true';
    if (on === row.subOut) return;
    const ids = new Set(row.rows.map(r => r.oid));
    setBusy(true);
    persist(tests.map(r => (ids.has(r.oid) ? { ...r, subOut: on } : r)))
      .catch(e => {
        const m = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message;
        notify.error(m ?? 'Could not update SubOut.');
      })
      .finally(() => setBusy(false));
  };

  /** Adds every parameter of the chosen tests, skipping any already assigned —
   *  the save SP refuses a duplicate (sample, Testparameter), so filtering here
   *  turns a hard 500 into the no-op the user expects. */
  const addTests = async (picked: PreLoginTestCatalogRow[]) => {
    const have = new Set(tests.map(r => r.testParameterOid).filter(Boolean));
    // A parameter can belong to two groups (or be listed twice in one): keep the first only.
    const seen = new Set<string>();
    const fresh = picked.filter(p => {
      if (have.has(p.testParameterOid) || seen.has(p.testParameterOid)) return false;
      seen.add(p.testParameterOid);
      return true;
    });

    setPickerOpen(false);
    if (fresh.length === 0) {
      notify.info('Those tests are already assigned to this sample.');
      return;
    }

    const added = fresh.map(p => ({
      oid: `new:${p.testParameterOid}`,
      sampleOid: sampleOid ?? '', cocOid: '',
      sampleID: null, matrix: p.matrix, test: p.test, testGroupName: p.isGroup ? p.test : null,
      method: p.method, components: p.component, subOut: false,
      testParams: null, testParameterOid: p.testParameterOid,
      testMethodOid: p.testMethodOid, groupTestOid: p.groupTestOid, isGroup: p.isGroup,
      preservativeOid: null, preservativeName: null,
      containerTypeOid: null, containerName: null, containerCount: null,
      dupBottle: null, subLabOid: null, subLabName: null,
      suboutQCTypeOid: null, suboutQCTypeName: null, subOutDate: null,
    } as unknown as PreLoginTest));

    setBusy(true);
    try {
      await persist([...tests, ...added]);
      notify.success('Test(s) added successfully.');
    } catch (e) {
      const m = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message;
      notify.error(m ?? 'Could not add the tests.');
    } finally { setBusy(false); }
  };

  /** Removes the selected TESTS — every parameter row behind each one. */
  const removeSelected = () => {
    if (sel.size === 0) return;
    const doomed = new Set(
      grouped.filter(g => sel.has(g.key)).flatMap(g => g.rows.map(r => r.oid)));
    const count = sel.size;
    void runDelete({
      formName: PRE_LOGIN_FORM,
      message: `Remove ${count} test${count === 1 ? '' : 's'} from this sample?`,
      confirmLabel: 'Remove',
      successMessage: 'Removed successfully.',
      delete: async () => {
        await persist(flattenBroken(tests, tests.filter(r => !doomed.has(r.oid))));
      },
    });
  };

  // The root carries NO inline margin or width: `.sr-sec` already sets
  // `margin: 0 0 8px`, and this card must dock exactly like the samples card
  // above it and the Tests / Containers / Parameters cards. Overriding either
  // here is what makes two stacked grids stop lining up.
  return (
    <section className="sr-sec">
      <h3 className="sr-sec-h">
        Test Assignment{sampleId ? ' — ' : ''}
        <span style={{ color: 'var(--ai-orange-hover, #e8670a)' }}>{sampleId ?? ''}</span>
        <span className="sr-sec-note">
          {sampleOid
            ? 'pick a sample row above to switch · Add assigns a test with all its parameters'
            : 'select a sample above to assign its tests'}
        </span>
      </h3>

      <div className="sr-sec-b">
        {/* The shared toolbar: Add · Remove first, then Refresh · Export to.
            noHotkeys because the page's main toolbar above owns Alt+N / Alt+D /
            Alt+R / Alt+X (last-registered wins). */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            showMeta={false}
            noHotkeys
            onNew={() => setPickerOpen(true)}
            newLabel="Add"
            newDisabled={!sampleOid || busy}
            onDelete={removeSelected}
            deleteDisabled={sel.size === 0 || busy}
            onRefresh={onRefresh}
            refreshDisabled={!sampleOid}
            exportData={{
              filename: 'Pre-Login Test Assignment',
              headers: ['Matrix', 'Test', 'TestGroupName', 'Method', 'Components', 'SubOut', 'Parameter'],
              rows: grouped.map(g => [g.matrix ?? '', g.test ?? '', g.testGroupName ?? '', g.method ?? '',
                                       g.component ?? '', g.subOut ? 'true' : 'false', g.parameter ?? '']),
            }}
          />
        </div>

        <AppDataGrid<AssignedTest>
          rows={grouped}
          columns={columns}
          getRowId={r => r.key}
          selectable
          selectedIds={sel}
          onSelectionChange={setSel}
          onCellCommit={onCellCommit}
          initialSort={{ key: 'test', dir: 'asc' }}
          autoFocusFirstRow={false}
          // This grid sits directly beneath the Samples grid, which is wide
          // enough to scroll horizontally and so paints across the reserved
          // scrollbar gutter. These six columns fit, so without this the two
          // grids' content would end 9px apart even though their cards and
          // grid boxes are pixel-identical (measured 2026-09-28).
          flushRight
          defaultPageSize={10}
          exportFilename="Pre-Login Test Assignment"
          emptyText={sampleOid
            ? 'No tests assigned to this sample — click Add.'
            : 'Select a sample above.'}
        />
      </div>

      {pickerOpen && sampleOid && (
        <TestCatalogDialog
          sampleOid={sampleOid}
          assigned={new Set(tests.map(r => r.testParameterOid).filter(Boolean) as string[])}
          onClose={() => setPickerOpen(false)}
          onAdd={rows => void addTests(rows)}
        />
      )}
    </section>
  );
}

/**
 * The "Add" picker — the tests this sample can be given, scoped to its matrix
 * by the server. The catalogue arrives at PARAMETER grain and is grouped here
 * into the tests a user picks; choosing one returns all of its parameter rows,
 * so the caller never has to go back for them.
 *
 * <p>A test whose parameters are ALL already assigned is shown but disabled
 * rather than hidden — hiding it would read as "this lab has no such test".</p>
 */
function TestCatalogDialog({ sampleOid, assigned, onClose, onAdd }: {
  sampleOid: string;
  assigned: Set<string>;
  onClose: () => void;
  onAdd: (rows: PreLoginTestCatalogRow[]) => void;
}) {
  const notify = useNotify();
  const [all, setAll]         = useState<PreLoginTestCatalogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('');
  const [picked, setPicked]   = useState<Set<string | number>>(new Set());

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/api/v1/pre-login-settings/samples/${sampleOid}/test-catalog`)
      .then(res => { if (alive) setAll(res.data?.data ?? []); })
      .catch(() => { if (alive) notify.error('Could not load the test catalogue.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sampleOid, notify]);

  interface CatalogTest {
    key: string; test: string; method: string; component: string;
    paramCount: number; allAssigned: boolean; rows: PreLoginTestCatalogRow[];
  }

  const tests: CatalogTest[] = useMemo(() => {
    const by = new Map<string, CatalogTest>();
    for (const r of all) {
      const key = `${r.testMethodOid}|${r.component ?? 'Default'}`;
      const hit = by.get(key);
      if (hit) { hit.rows.push(r); hit.paramCount = hit.rows.length; }
      else {
        by.set(key, {
          key, test: r.test ?? '', method: r.method ?? '',
          component: r.component ?? 'Default', paramCount: 1,
          allAssigned: false, rows: [r],
        });
      }
    }
    const list = [...by.values()];
    for (const t of list) t.allAssigned = t.rows.every(r => assigned.has(r.testParameterOid));

    const q = filter.trim().toLowerCase();
    return q
      ? list.filter(t => `${t.test} ${t.method} ${t.component}`.toLowerCase().includes(q))
      : list;
  }, [all, assigned, filter]);

  const columns: AppColumn<CatalogTest>[] = useMemo(() => [
    { field: 'test',       header: 'Test',       width: 240 },
    { field: 'method',     header: 'Method',     width: 150 },
    { field: 'component',  header: 'Component',  width: 140 },
    { field: 'paramCount', header: 'Parameters', width: 100, align: 'right' },
    {
      field: 'allAssigned', header: '', width: 110,
      render: (t: CatalogTest) => t.allAssigned
        ? <span style={{ color: 'var(--ai-muted)' }}>assigned</span>
        : null,
      text: (t: CatalogTest) => (t.allAssigned ? 'assigned' : ''),
    },
  ], []);

  const confirm = () => {
    const chosen = tests.filter(t => picked.has(t.key) && !t.allAssigned);
    if (chosen.length === 0) { notify.warning('Select at least one test.'); return; }
    onAdd(chosen.flatMap(t => t.rows));
  };

  return (
    <AppDialog
      open
      title="Add tests"
      icon="ti-microscope"
      onClose={onClose}
      width={860}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        {/* .ca-btn-solid marks the primary, which is what Enter activates
            (standing rule 14 — the dialog owns its own Enter/Esc). */}
        <button className="ca-btn ca-btn-solid" onClick={confirm}>
          <i className="ti ti-plus" /> Add
        </button>
      </>}
    >
      <div style={{ marginBottom: 8 }}>
        <input
          className="sr-input"
          placeholder="Search test, method or component…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{ width: '100%' }}
        />
      </div>
      <AppDataGrid<CatalogTest>
        rows={tests}
        columns={columns}
        getRowId={t => t.key}
        loading={loading}
        selectable
        selectedIds={picked}
        onSelectionChange={setPicked}
        defaultPageSize={10}
        emptyText="No tests match — this sample's matrix decides what is offered."
      />
    </AppDialog>
  );
}
